import React from "react";
import EmptyState from "../common/EmptyState";
import LoadingSpinner from "../common/LoadingSpinner";

interface AssignmentPanelProps {
  title: string;
  // The count line under the title, e.g. "12 users"
  countLabel: string;
  // "available" is the neutral left column, "assigned" the branded right one
  tone: "available" | "assigned";
  loading?: boolean;
  isEmpty: boolean;
  emptyIcon: React.ComponentType<{ className?: string }>;
  emptyTitle: string;
  // Infinite scroll for the paginated column
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  loadingMore?: boolean;
  loadingMoreLabel?: string;
  children: React.ReactNode;
}

const HEADER: Record<AssignmentPanelProps["tone"], string> = {
  available:
    "border-b border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-navy-900",
  assigned:
    "border-b border-gray-200 bg-biafit-primary p-4 dark:border-gray-800",
};

const TITLE: Record<AssignmentPanelProps["tone"], string> = {
  available: "text-lg font-medium text-navy-700 dark:text-white",
  assigned: "text-lg font-medium text-white",
};

const COUNT: Record<AssignmentPanelProps["tone"], string> = {
  available: "text-sm text-gray-600 dark:text-gray-400",
  assigned: "text-sm text-white opacity-90",
};

// One column of an assignment modal: a titled header with a count line over
// a fixed-height scrolling list, with the spinner and empty state built in.
const AssignmentPanel: React.FC<AssignmentPanelProps> = ({
  title,
  countLabel,
  tone,
  loading = false,
  isEmpty,
  emptyIcon,
  emptyTitle,
  onScroll,
  loadingMore = false,
  loadingMoreLabel = "Loading more...",
  children,
}) => (
  <div
    className={`min-h-[200px] flex-1 overflow-hidden ${
      tone === "available"
        ? "border-r border-gray-200 dark:border-gray-800"
        : ""
    }`}
  >
    <div className={HEADER[tone]}>
      <h3 className={TITLE[tone]}>{title}</h3>
      <p className={COUNT[tone]}>{countLabel}</p>
    </div>

    <div className="h-80 overflow-y-auto p-4" onScroll={onScroll}>
      {loading ? (
        <LoadingSpinner size="lg" />
      ) : isEmpty ? (
        <EmptyState icon={emptyIcon} title={emptyTitle} padding="sm" />
      ) : (
        <div className="space-y-2">
          {children}

          {loadingMore && (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner bare size="md" />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                {loadingMoreLabel}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

export default AssignmentPanel;
